import { Component, effect, inject, signal } from '@angular/core';
import { DialogActionsComponent, DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  CapitalizePipe,
  DocumentFeeType,
  DocumentFeeTypeService,
  errorToast,
  FormErrorService,
  successSaveToast,
  SupplierCreditNoteFee,
  SupplierCreditNoteFeeService,
  ValidationDirective,
  DropdownLoaderDirective,
} from 'shared';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, take } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ComboBoxComponent, DropDownFilterSettings, FilterDirective } from '@progress/kendo-angular-dropdowns';
import { distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { FormFieldComponent, NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'rc-supplier-credit-note-fee-popup',
  imports: [
    ButtonComponent,
    CapitalizePipe,
    ComboBoxComponent,
    DialogActionsComponent,
    FormFieldComponent,
    LabelComponent,
    ReactiveFormsModule,
    TranslateModule,
    ValidationDirective,
    FilterDirective,
    NumericTextBoxComponent,
    AsyncPipe,
    DropdownLoaderDirective,
  ],
  providers: [FormErrorService, TranslateService, TranslatePipe, CapitalizePipe],
  templateUrl: './supplier-credit-note-fee-popup.component.html',
  styleUrl: './supplier-credit-note-fee-popup.component.scss',
})
export class SupplierCreditNoteFeePopupComponent extends DialogContentBase {
  #supplierCreditNoteFeeService = inject(SupplierCreditNoteFeeService);
  #notificationService = inject(NotificationService);
  #formErrorService = inject(FormErrorService);
  #capitalizePipe = inject(CapitalizePipe);
  #translateService = inject(TranslateService);
  #documentFeeService = inject(DocumentFeeTypeService);
  #filterSubject = new Subject<string>();
  supplierCreditNote = signal<string | undefined>(undefined);

  supplierCreditNoteFeeForm: FormGroup<SupplierCreditNoteFeeForm> = this.#initForm();
  documentFeeTypes$ = this.#documentFeeService.getDocumentFeeTypes();

  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: 2,
  };

  constructor() {
    const dialog = inject(DialogRef);

    super(dialog);

    effect(() => {
      if (this.supplierCreditNote()) {
        this.supplierCreditNoteFeeForm.patchValue({
          supplierCreditNote: this.supplierCreditNote(),
        });
      }
    });

    this.#filterSubject.pipe(debounceTime(300)).subscribe((value) => {
      this.getDocumentFeeTypes(value);
    });
  }

  #initForm() {
    const form = new FormGroup<SupplierCreditNoteFeeForm>({
      supplierCreditNote: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      type: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      amount: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
    form.updateValueAndValidity();
    return form;
  }

  getDocumentFeeTypes(value: string) {
    this.documentFeeTypes$ = this.#documentFeeService.findDocumentFeeTypes(`translations.name=${value}`);
  }

  onSubmit() {
    this.#supplierCreditNoteFeeService
      .create(this.supplierCreditNoteFeeForm.value as SupplierCreditNoteFee)
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: () => {
          const notification = this.#notificationService.show(successSaveToast);
          if (notification.content) {
            notification.content.instance.value = this.#capitalizePipe.transform(
              this.#translateService.instant('EXTRA_COST'),
            );
          }
          this.dialog.close({ isSuccessFull: true });
        },
        error: (err: HttpErrorResponse) => {
          const notification = this.#notificationService.show(errorToast);
          if (notification.content) {
            notification.content.instance.setCurrentValue(err);
          }
          this.#formErrorService.setErrorViolations(err);
        },
      });
  }

  close() {
    this.supplierCreditNoteFeeForm.reset();
    this.dialog.close();
  }

  getData(entries: DocumentFeeType[] | null) {
    if (entries) {
      return entries;
    }
    return [];
  }

  onSearch(value: string) {
    this.#filterSubject.next(value);
  }
}

interface SupplierCreditNoteFeeForm {
  supplierCreditNote: FormControl<string | null>;
  amount: FormControl<number>;
  type: FormControl<string>;
}
